import { InvestorDetailsCommitmentsTabTableDataQuery } from '@/__generated__/graphql';
import { InfoCircleIcon } from '@/assets/static/icons/info-circle';
import { Tooltip } from '@/components/basicComponents/tooltip';
import FormattingTooltip from '@/components/basicComponents/tooltip/dollarsTooltip';
import NoResults from '@/components/table/noResults';
import RedirectTitleCell from '@/components/table/redirectTitleCell';
import { TableBodyWrapper, TableRowCell, TableRowWrapper, TextCell } from '@/components/table/styles';
import { TableColumns } from '@/components/table/types';
import { currencyFormatter, formatCash } from '@/shared/utils/currency';
import { getUTCDate } from '@/shared/utils/date';
import { themeVar } from '@/styles/theme';
import styled from 'styled-components';
import { InvestorDetailsEntityTypeEnum } from '../../../types';

interface CommitmentsTableBodyProps {
  tableColumns: TableColumns[];
  tableRows: InvestorDetailsCommitmentsTabTableDataQuery['ClientEntityDetailsCommitments']['commitments'];
  entityType: InvestorDetailsEntityTypeEnum;
  openOpportunityDetailsPage: (id: string) => void;
}

const CommitmentsTableBody = ({ tableColumns, tableRows, entityType, openOpportunityDetailsPage }: CommitmentsTableBodyProps) => {
  if (!tableRows?.length) {
    return <NoResults />;
  }

  const getColorStatus = (status: string) => {
    if (status === 'Active') {
      return '#3DBD4A';
    } else if (status === 'Recommended' || status === 'Outstanding') {
      return '#DFC902';
    } else {
      return '#111827';
    }
  };

  const toolTipContent = (row: InvestorDetailsCommitmentsTabTableDataQuery['ClientEntityDetailsCommitments']['commitments'][number]) => {
    return (
      <TableBodyWrapper>
        <TableRowCell width={100}>
          <TextCell>{formatCash(row.committedCapital)} Committed Capital</TextCell>
        </TableRowCell>
        <TableRowCell width={100}>
          <TextCell>{formatCash(row.capitalCalled)} Capital Called</TextCell>
        </TableRowCell>
        <TableRowCell width={100}>
          <TextCell>{formatCash(row.capitalRemaining)} Capital Remaining</TextCell>
        </TableRowCell>
        <TableRowCell width={100}>
          <TextCell>{((row.capitalCalled / row.committedCapital) * 100).toFixed(2)}% Called to Date</TextCell>
        </TableRowCell>
      </TableBodyWrapper>
    );
  };

  const marketValueToolTipContent = (row: InvestorDetailsCommitmentsTabTableDataQuery['ClientEntityDetailsCommitments']['commitments'][number]) => {
    return (
      <TableBodyWrapper>
        {row.valuationDate && (
          <>
            <TableRowCell width={100}>
              <TextCell>Valuation Date: {row.valuationAmount ? getUTCDate(row.valuationDate) : 'None'}</TextCell>
            </TableRowCell>
            {row.valuationAmount !== null && (
              <TableRowCell width={100}>
                <TextCell>Valuation Amount: {currencyFormatter.format(row.valuationAmount)}</TextCell>
              </TableRowCell>
            )}
          </>
        )}
        {row.aggregateCallsAndDistributions !== null && (
          <TableRowCell width={100}>
            <TextCell>
              Aggregate Calls and Distributions since last valuation: {currencyFormatter.format(row.aggregateCallsAndDistributions)}
            </TextCell>
          </TableRowCell>
        )}
        <TableRowCell width={100}>
          <TextCell>Market Value: {currencyFormatter.format(row.marketValue)}</TextCell>
        </TableRowCell>
      </TableBodyWrapper>
    );
  };

  return (
    <>
      {tableRows.map((row) => (
        <TableRowWrapper key={row.id}>
          <CustomRowCell width={tableColumns[0].width}>
            <RedirectTitleCell id="actionCurioText" onClick={() => openOpportunityDetailsPage(row.investmentId)}>
              {row.name}
            </RedirectTitleCell>
            {(entityType === InvestorDetailsEntityTypeEnum.Household || entityType === InvestorDetailsEntityTypeEnum.Group) && (
              <EntityName>{row.entityName}</EntityName>
            )}
          </CustomRowCell>
          <TableRowCell width={tableColumns[1].width}>
            <TextCell>{row.subAssetClass ? `${row.assetClass?.name} - ${row.subAssetClass.name}` : row.assetClass?.name}</TextCell>
          </TableRowCell>
          <TableRowCell width={tableColumns[2].width}>
            <TextCell>
              <FormattingTooltip>{row.committedCapital}</FormattingTooltip>
            </TextCell>
          </TableRowCell>
          <TableRowCell width={tableColumns[3].width}>
            <TextCell>
              <CellWithTooltip>
                {((row.capitalCalled / row.committedCapital) * 100).toFixed(2)}%
                <TooltipWrapper id="infoWrap">
                  <Tooltip title={toolTipContent(row)}>
                    <InfoCircleIcon fill={themeVar('font.disabled')} style={{ position: 'relative', top: '3px' }} />
                  </Tooltip>
                </TooltipWrapper>
              </CellWithTooltip>
            </TextCell>
          </TableRowCell>
          <TableRowCell width={tableColumns[4].width}>
            <TextCell>
              <FormattingTooltip>{+row.distributionsToDate}</FormattingTooltip>
            </TextCell>
          </TableRowCell>
          <TableRowCell width={tableColumns[5].width}>
            <TextCell>
              {row.valuationDate || row.valuationAmount || row.aggregateCallsAndDistributions || row.marketValue ? (
                <CellWithTooltip>
                  {formatCash(row.marketValue)}
                  <TooltipWrapper id="infoWrap">
                    <Tooltip title={marketValueToolTipContent(row)}>
                      <InfoCircleIcon fill={themeVar('font.disabled')} style={{ position: 'relative', top: '3px' }} />
                    </Tooltip>
                  </TooltipWrapper>
                </CellWithTooltip>
              ) : (
                <FormattingTooltip>{row.marketValue}</FormattingTooltip>
              )}
            </TextCell>
          </TableRowCell>
          <TableRowCell width={tableColumns[6].width}>
            <ColorTitle color={getColorStatus(row.status)}>{row.status === 'Outstanding' ? 'Pending Commitment' : row.status}</ColorTitle>
          </TableRowCell>
        </TableRowWrapper>
      ))}
    </>
  );
};

export default CommitmentsTableBody;

const CustomRowCell = styled(TableRowCell)`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 0;
`;

const EntityName = styled(TextCell)`
  color: ${themeVar('font.strong')};
`;

const ColorTitle = styled(TextCell)<{ color: string }>`
  color: ${({ color }) => color};
  font-weight: 600;
`;

const CellWithTooltip = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TooltipWrapper = styled.div`
  opacity: 0;
`;
